import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Star } from "lucide-react";

/**
 * Conditional homepage reviews section.
 *
 * Renders **nothing** if /api/reviews/public returns zero approved reviews.
 * We never seed fake placeholders here.
 */
export default function HomeReviewsSection() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/reviews/public")
            .then((r) => setData(r.data))
            .catch(() => setData({ reviews: [], count: 0 }));
    }, []);

    if (!data || data.count === 0) return null;

    const top = data.reviews.slice(0, 3);

    return (
        <section className="a1-section" data-testid="home-reviews-section">
            <div className="a1-container">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                    <div>
                        <p className="text-xs uppercase tracking-[0.22em] font-bold mb-1"
                           style={{ color: "var(--color-accent)" }}>
                            Customer reviews
                        </p>
                        <h2 className="text-3xl sm:text-4xl m-0" style={{ color: "var(--color-text)" }}>
                            What customers say
                        </h2>
                    </div>
                    <Link to="/reviews" className="a1-btn-secondary" data-testid="home-reviews-see-all">
                        See all reviews
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {top.map((r) => (
                        <article key={r.id} className="a1-card" data-testid={`home-review-card-${r.id}`}>
                            <div className="flex items-center gap-0.5 mb-2">
                                {[1,2,3,4,5].map((n) => (
                                    <Star key={n} size={16}
                                          fill={n <= r.rating ? "var(--color-cta)" : "transparent"}
                                          color="var(--color-cta)" strokeWidth={1.5} />
                                ))}
                            </div>
                            <p className="text-sm" style={{ color: "var(--color-text)" }}>
                                "{r.review_text.length > 200 ? r.review_text.slice(0, 197) + "…" : r.review_text}"
                            </p>
                            <p className="text-xs mt-3 font-semibold" style={{ color: "var(--color-text)" }}>
                                — {r.customer_name}
                                {r.city_or_area ? (
                                    <span className="font-normal" style={{ color: "var(--color-text-muted)" }}>
                                        {" · "}{r.city_or_area}
                                    </span>
                                ) : null}
                            </p>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}
